/**
 * Error Boundary Component
 * Catches and displays errors gracefully without crashing the entire app
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { trackError } from '@/lib/tracking-helpers';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ERROR BOUNDARY] Caught error:', error);
    console.error('[ERROR BOUNDARY] Error message:', error?.message);
    console.error('[ERROR BOUNDARY] Error stack:', error?.stack);
    console.error('[ERROR BOUNDARY] Component stack:', errorInfo?.componentStack);

    // Track error in analytics
    trackError(
      error.message,
      error.stack,
      `React Component: ${errorInfo.componentStack?.split('\n')[1]?.trim() || 'Unknown'}`
    );

    this.setState({
      error,
      errorInfo
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen w-full flex items-center justify-center bg-gray-50 p-4">
          <Card className="max-w-2xl w-full border-[#4B5320] bg-red-50">
            <CardHeader className="border-b border-[#4B5320]">
              <CardTitle className="flex items-center gap-2 text-red-900">
                <AlertTriangle className="w-6 h-6" />
                Something Went Wrong
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <p className="text-red-800">
                  The application encountered an unexpected error. This has been logged for review.
                </p>

                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <details className="bg-white p-4 rounded border border-[#4B5320]">
                    <summary className="cursor-pointer font-semibold text-red-900 mb-2">
                      Error Details (Development Only)
                    </summary>
                    <div className="space-y-2 text-sm">
                      <div>
                        <strong className="text-red-800">Error:</strong>
                        <pre className="mt-1 p-2 bg-gray-100 rounded text-xs overflow-auto">
                          {this.state.error.toString()}
                        </pre>
                      </div>
                      {this.state.errorInfo && (
                        <div>
                          <strong className="text-red-800">Component Stack:</strong>
                          <pre className="mt-1 p-2 bg-gray-100 rounded text-xs overflow-auto">
                            {this.state.errorInfo.componentStack}
                          </pre>
                        </div>
                      )}
                    </div>
                  </details>
                )}

                <div className="flex gap-3">
                  <Button
                    onClick={this.handleReset}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reload Application
                  </Button>
                  <Button
                    onClick={() => window.history.back()}
                    variant="outline"
                    className="border-[#4B5320] text-red-700 hover:bg-red-50"
                  >
                    Go Back
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
